<template>
  <transition name="department-picker-dialog" appear>
    <div
      v-if="value"
      class="department-picker-dialog__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="department-picker-dialog-title"
      @mousedown.self="handleClose"
    >
      <div class="department-picker-dialog__card">
        <div class="department-picker-dialog__header">
          <div class="department-picker-dialog__header-icon" aria-hidden="true">
            <Icon name="department_featured_icon" />
          </div>
          <button
            type="button"
            class="department-picker-dialog__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="department-picker-dialog__intro">
          <span class="department-picker-dialog__title"> Assign to department </span>
          <span class="department-picker-dialog__subtitle">
            Choose the department this position will belong to.
          </span>
        </div>

        <div v-if="!selectedDepartment" class="department-picker-dialog__search-wrap">
          <ExTextInput
            ref="searchInputRef"
            label=""
            ghostText="Search departments by name or code"
            icon="interface_search"
            clearText="clear search"
            class="department-picker-dialog__search-input"
            :value="searchQuery"
            @input="handleSearchInput"
          />
        </div>

        <div v-else class="department-picker-dialog__picked-wrap">
          <div class="department-picker-dialog__picked">
            <Icon name="department_picker_icon" class="department-picker-dialog__picked-icon" />
            <div class="department-picker-dialog__breadcrumb">
              <span
                v-for="(seg, i) in breadcrumbSegments"
                :key="i"
                class="department-picker-dialog__breadcrumb-item"
              >
                <span
                  :class="
                    i === breadcrumbSegments.length - 1
                      ? 'department-picker-dialog__breadcrumb-leaf'
                      : 'department-picker-dialog__breadcrumb-ancestor'
                  "
                >
                  {{ seg }}
                </span>
                <span
                  v-if="i < breadcrumbSegments.length - 1"
                  class="department-picker-dialog__breadcrumb-sep"
                >
                  ›
                </span>
              </span>
            </div>
            <span class="department-picker-dialog__code">#{{ selectedDepartment.code }}</span>
            <div
              class="department-picker-dialog__clear"
              role="button"
              aria-label="Clear selection"
              @click.stop="clearSelection"
            >
              <Icon name="x_close_icon" />
            </div>
          </div>
        </div>

        <div
          v-if="!selectedDepartment"
          class="department-picker-dialog__content"
          :class="{ 'department-picker-dialog__content--list': showResultsList }"
        >
          <div v-if="showEmptyState" class="department-picker-dialog__empty-state">
            <span class="department-picker-dialog__empty-title">No departments yet</span>
            <span class="department-picker-dialog__empty-subtitle">
              Create a department first.
            </span>
          </div>

          <ul
            v-else-if="showSkeleton"
            class="department-picker-dialog__skeleton-list"
            aria-hidden="true"
          >
            <li
              v-for="(barWidth, index) in skeletonBarWidths"
              :key="`skeleton-${index}`"
              class="department-picker-dialog__skeleton-row"
            >
              <span class="department-picker-dialog__skeleton-icon" />
              <span class="department-picker-dialog__skeleton-bar" :style="{ width: barWidth }" />
            </li>
          </ul>

          <div v-else-if="showNoResults" class="department-picker-dialog__empty-state">
            <span class="department-picker-dialog__empty-title">No matches</span>
            <span class="department-picker-dialog__empty-subtitle">
              Try a different name or code
            </span>
          </div>

          <ul
            v-else
            ref="listRef"
            class="department-picker-dialog__list"
            role="listbox"
            aria-label="Departments"
            @scroll="handleListScroll"
          >
            <li
              v-for="item in displayItems"
              :key="item.id"
              role="option"
              :aria-selected="isSelected(item)"
              class="department-picker-dialog__row"
              :class="{ 'department-picker-dialog__row--disabled': isItemDisabled(item) }"
              @click.stop="selectDepartment(item)"
            >
              <Icon name="department_picker_icon" class="department-picker-dialog__row-icon" />
              <div class="department-picker-dialog__row-path">
                <span
                  v-for="(seg, i) in getPathSegments(item)"
                  :key="`${item.id}-${i}`"
                  class="department-picker-dialog__row-path-item"
                >
                  <span
                    :class="
                      i === getPathSegments(item).length - 1
                        ? 'department-picker-dialog__row-path-leaf'
                        : 'department-picker-dialog__row-path-ancestor'
                    "
                  >
                    {{ seg }}
                  </span>
                  <span
                    v-if="i < getPathSegments(item).length - 1"
                    class="department-picker-dialog__row-path-sep"
                  >
                    ›
                  </span>
                </span>
              </div>
              <span class="department-picker-dialog__row-code">#{{ item.code }}</span>
            </li>
            <li
              v-if="isLoadingMore"
              class="department-picker-dialog__list-loading"
              role="status"
              aria-live="polite"
            >
              <span>Loading more…</span>
            </li>
          </ul>
        </div>

        <div class="department-picker-dialog__footer">
          <p
            v-if="error"
            class="department-picker-dialog__error"
            role="alert"
            data-testid="department-picker-dialog-error"
          >
            {{ error }}
          </p>
          <div class="department-picker-dialog__footer-actions">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Cancel"
              @click="handleClose"
            />
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              :label="loading ? 'Assigning...' : 'Assign'"
              :disabled="!selectedDepartment || loading"
              :loaderLeft="loading"
              @click="handleAssign"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import Icon from "@components/icons/Icon.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import {
  MOCK_DEPARTMENT_TREE,
  DEPARTMENT_SEARCH_DELAY_MS,
  getSkeletonBarWidths,
  searchMockDepartments,
} from "./departmentPickerMockData.js";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  hasMoreData: {
    type: Boolean,
    default: false,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabledIds: {
    type: Array,
    default: () => [],
  },
  useStaticSearch: {
    type: Boolean,
    default: true,
  },
  searchDelayMs: {
    type: Number,
    default: DEPARTMENT_SEARCH_DELAY_MS,
  },
  /**
   * Real department tree from the API. When omitted, the mock tree is used (Storybook / demo).
   * `null` means the tree is still loading; `[]` means the account has no departments.
   */
  departmentTree: {
    type: Array,
    default: undefined,
  },
  /** Inline server error (Rule 10): shown in the footer while the dialog stays open. */
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["input", "close", "assign", "search", "load-more", "item-selected"]);

const searchQuery = ref("");
const selectedDepartment = ref(null);
const searchInputRef = ref(null);
const listRef = ref(null);
const isSearchLoading = ref(false);
const internalSearchResults = ref([]);
const skeletonBarWidths = getSkeletonBarWidths();
let searchDebounceTimeout = null;
let searchLoadTimeout = null;

const trimmedSearchQuery = computed(() => searchQuery.value.trim());

const displayItems = computed(() =>
  props.useStaticSearch ? internalSearchResults.value : props.items
);

const resolvedTree = computed(() =>
  props.departmentTree === undefined ? MOCK_DEPARTMENT_TREE : props.departmentTree
);

const isLoadingResults = computed(() =>
  props.useStaticSearch ? isSearchLoading.value || resolvedTree.value === null : props.isLoading
);

const showEmptyState = computed(
  () => !trimmedSearchQuery.value && !isLoadingResults.value && !displayItems.value.length
);

const showSkeleton = computed(() => isLoadingResults.value);

const showNoResults = computed(
  () => !!trimmedSearchQuery.value && !isLoadingResults.value && !displayItems.value.length
);

const showResultsList = computed(
  () => isLoadingResults.value || displayItems.value.length > 0 || props.isLoadingMore
);

const breadcrumbSegments = computed(() => {
  if (!selectedDepartment.value) return [];
  const ancestors = Array.isArray(selectedDepartment.value.path)
    ? selectedDepartment.value.path.map((p) => p.name)
    : [];
  return buildTruncatedSegments([...ancestors, selectedDepartment.value.name]);
});

watch(
  () => props.value,
  (open) => {
    if (open) {
      resetState();
      loadDefaultList();
      nextTick(() => {
        searchInputRef.value?.focus();
      });
    }
  },
  { immediate: true }
);

watch(resolvedTree, () => {
  if (!props.value || !props.useStaticSearch || selectedDepartment.value) return;
  runStaticSearch(trimmedSearchQuery.value);
});

function clearSearchTimers() {
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
    searchDebounceTimeout = null;
  }
  if (searchLoadTimeout) {
    clearTimeout(searchLoadTimeout);
    searchLoadTimeout = null;
  }
}

function resetState() {
  searchQuery.value = "";
  selectedDepartment.value = null;
  internalSearchResults.value = [];
  isSearchLoading.value = false;
  clearSearchTimers();
}

function buildTruncatedSegments(names) {
  if (names.length <= 3) return names;
  return [names[0], "…", names[names.length - 1]];
}

function getPathSegments(dept) {
  if (!dept) return [];
  const ancestors = Array.isArray(dept.path) ? dept.path.map((p) => p.name) : [];
  return buildTruncatedSegments([...ancestors, dept.name]);
}

function runStaticSearch(query) {
  if (searchLoadTimeout) {
    clearTimeout(searchLoadTimeout);
    searchLoadTimeout = null;
  }

  isSearchLoading.value = true;
  searchLoadTimeout = setTimeout(() => {
    internalSearchResults.value = searchMockDepartments(resolvedTree.value, query);
    isSearchLoading.value = false;
    searchLoadTimeout = null;
  }, props.searchDelayMs);
}

/** Empty query = every eligible department; the parent fetches it in API-backed mode. */
function loadDefaultList() {
  internalSearchResults.value = [];
  emit("search", "");
  if (props.useStaticSearch) {
    runStaticSearch("");
  }
}

function isSelected(item) {
  return selectedDepartment.value?.id === item?.id;
}

function isItemDisabled(item) {
  if (!item?.id) return true;
  return props.disabledIds.includes(item.id);
}

function selectDepartment(dept) {
  if (isItemDisabled(dept)) return;
  searchQuery.value = "";
  internalSearchResults.value = [];
  isSearchLoading.value = false;
  clearSearchTimers();
  selectedDepartment.value = dept;
  emit("item-selected", dept);
}

function clearSelection() {
  selectedDepartment.value = null;
  emit("item-selected", null);
  loadDefaultList();
  nextTick(() => {
    searchInputRef.value?.focus();
  });
}

function handleSearchInput(value) {
  searchQuery.value = value ?? "";

  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
    searchDebounceTimeout = null;
  }
  if (searchLoadTimeout) {
    clearTimeout(searchLoadTimeout);
    searchLoadTimeout = null;
  }

  const query = trimmedSearchQuery.value;
  if (!query) {
    loadDefaultList();
    return;
  }

  internalSearchResults.value = [];
  isSearchLoading.value = true;

  searchDebounceTimeout = setTimeout(() => {
    emit("search", query);

    if (props.useStaticSearch) {
      runStaticSearch(query);
    }

    searchDebounceTimeout = null;
  }, 300);
}

function handleListScroll(event) {
  if (!props.hasMoreData || props.isLoadingMore) return;

  const target = event.target;
  const threshold = 24;
  const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

  if (reachedBottom) {
    emit("load-more");
  }
}

function handleClose() {
  resetState();
  emit("input", false);
  emit("close");
}

function handleAssign() {
  if (!selectedDepartment.value || props.loading) return;
  emit("assign", { department: selectedDepartment.value });
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) {
    handleClose();
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  clearSearchTimers();
});
</script>

<style scoped>
.department-picker-dialog-enter-active,
.department-picker-dialog-leave-active {
  transition: opacity 180ms ease;
}

.department-picker-dialog-enter-from,
.department-picker-dialog-leave-to {
  opacity: 0;
}

.department-picker-dialog-enter-active .department-picker-dialog__card,
.department-picker-dialog-leave-active .department-picker-dialog__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.department-picker-dialog-enter-from .department-picker-dialog__card,
.department-picker-dialog-leave-to .department-picker-dialog__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .department-picker-dialog-enter-active,
  .department-picker-dialog-leave-active,
  .department-picker-dialog-enter-active .department-picker-dialog__card,
  .department-picker-dialog-leave-active .department-picker-dialog__card {
    transition: none !important;
  }
}

.department-picker-dialog__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.department-picker-dialog__card {
  width: 100%;
  max-width: 788px;
  border-radius: 12px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  overflow: hidden;
}

.department-picker-dialog__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 0;
}

.department-picker-dialog__header-icon {
  display: flex;
  width: 48px;
  height: 48px;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  border: 1px solid #e9d7fe;
  background: #f9f5ff;
  box-shadow: 0 6px 16px -6px rgba(127, 86, 217, 0.25);
  color: #6941c6;
}

.department-picker-dialog__header-icon :deep(svg path),
.department-picker-dialog__header-icon :deep(svg rect) {
  stroke: currentColor;
}

.department-picker-dialog__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 8px;
  margin: -4px -4px 0 0;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 8px;
}

.department-picker-dialog__close-btn:hover {
  background: rgba(152, 162, 179, 0.1);
}

.department-picker-dialog__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.department-picker-dialog__intro {
  display: flex;
  flex-direction: column;
  padding: 16px 24px 0;
}

.department-picker-dialog__title {
  color: #101828;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}

.department-picker-dialog__subtitle {
  color: #667085;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.department-picker-dialog__search-wrap,
.department-picker-dialog__picked-wrap {
  padding: 16px 24px 0;
}

.department-picker-dialog__search-wrap :deep(.main_label) {
  display: none;
}

.department-picker-dialog__search-wrap :deep(.ex_text_input .input-search) {
  margin-top: 0 !important;
}

.department-picker-dialog__content {
  min-height: 220px;
  padding: 16px 24px 0;
}

.department-picker-dialog__content--list {
  min-height: 0;
  padding-bottom: 0;
}

.department-picker-dialog__empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  text-align: center;
}

.department-picker-dialog__empty-title {
  display: block;
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.department-picker-dialog__empty-subtitle {
  display: block;
  margin-top: 4px;
  color: #98a2b3;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.department-picker-dialog__skeleton-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.department-picker-dialog__skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
}

.department-picker-dialog__skeleton-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f2f4f7 0%, #eaecf0 50%, #f2f4f7 100%);
  background-size: 200% 100%;
  animation: department-picker-dialog-shimmer 1.4s ease-in-out infinite;
}

.department-picker-dialog__skeleton-bar {
  display: block;
  height: 12px;
  max-width: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #f2f4f7 0%, #eaecf0 50%, #f2f4f7 100%);
  background-size: 200% 100%;
  animation: department-picker-dialog-shimmer 1.4s ease-in-out infinite;
}

@keyframes department-picker-dialog-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.department-picker-dialog__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: scroll;
}

.department-picker-dialog__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 0;
  cursor: pointer;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.department-picker-dialog__row:last-child {
  margin-bottom: 0;
}

.department-picker-dialog__row--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.department-picker-dialog__row-icon {
  flex-shrink: 0;
  opacity: 0.6;
}

.department-picker-dialog__row-path {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
}

.department-picker-dialog__row-path-item {
  display: inline-flex;
  align-items: center;
}

.department-picker-dialog__row-path-ancestor {
  color: #667085;
  font-weight: 400;
}

.department-picker-dialog__row-path-sep {
  color: #d0d5dd;
  font-weight: 400;
  margin: 0 6px;
}

.department-picker-dialog__row-path-leaf {
  color: #101828;
  font-weight: 500;
}

.department-picker-dialog__row-code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #98a2b3;
  line-height: 20px;
  white-space: nowrap;
}

.department-picker-dialog__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.department-picker-dialog__picked-icon {
  flex-shrink: 0;
}

.department-picker-dialog__breadcrumb {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  font-size: 14px;
  line-height: 20px;
}

.department-picker-dialog__breadcrumb-item {
  display: inline-flex;
  align-items: center;
}

.department-picker-dialog__breadcrumb-ancestor {
  color: #475467;
  font-weight: 400;
}

.department-picker-dialog__breadcrumb-sep {
  color: #d0d5dd;
  font-weight: 400;
  margin: 0 4px;
}

.department-picker-dialog__breadcrumb-leaf {
  color: #6941c6;
  font-weight: 600;
}

.department-picker-dialog__code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #475467;
  line-height: 20px;
  white-space: nowrap;
}

.department-picker-dialog__clear {
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #98a2b3;
  padding: 2px;
  border-radius: 4px;
}

.department-picker-dialog__clear:hover {
  color: #667085;
  background: #f2f4f7;
}

.department-picker-dialog__list-loading {
  padding: 12px 16px;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  text-align: center;
}

.department-picker-dialog__footer {
  margin-top: 24px;
  padding: 16px 24px 24px;
  border-top: 1px solid #e5e7eb;
}

.department-picker-dialog__error {
  margin: 0 0 12px;
  color: #b42318;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.department-picker-dialog__footer-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}

.department-picker-dialog__footer-actions :deep(.standard-button) {
  flex: 1 1 0;
  min-width: 0;
}

.department-picker-dialog__footer-actions :deep(.standard-button button) {
  width: 100%;
}
</style>
